import React, { useEffect, useState } from "react";
import { Checkbox, InputNumber, Button, Card, Row, Col, message } from "antd";
import bitLabels from "./bitLabels.json"; // <-- your JSON

// Toggle this to see console logs for debugging
const DEV_LOG = false;

// Helpers
const setBit = (original: number, position: number, bitValue: number): number => {
  const mask = 1n << BigInt(position);
  let value = BigInt(original);
  if (bitValue === 1) value |= mask;
  else value &= ~mask;
  return Number(value);
};
const getBit = (value: number, position: number): number =>
  Number((BigInt(value) >> BigInt(position)) & 1n);

// Build a flat map of label definitions for quick access
type LabelDef = { label: string; setChildren?: number[]; unsetChildren?: number[] };
const buildFlatMap = (grouped: any): Record<number, LabelDef> => {
  const flat: Record<number, LabelDef> = {};
  for (const group of Object.values(grouped) as Record<string, unknown>[]) {
    for (const [k, v] of Object.entries(group)) {
      flat[Number(k)] = typeof v === "object" ? (v as LabelDef) : { label: String(v) };
    }
  }
  return flat;
};

const CtrlDoc: React.FC = () => {
  const categories = Object.entries(bitLabels);
  const flatMap = buildFlatMap(bitLabels);

  // compute robust bitCount: max index + 1 (handles sparse indices)
  const allIndexes = Object.keys(flatMap).map((k) => Number(k));
  const bitCount = allIndexes.length ? Math.max(...allIndexes) + 1 : 0;

  const [value, setValue] = useState<number>(0);
  const [bits, setBits] = useState<boolean[]>(Array(bitCount).fill(false));
  const [disabledBits, setDisabledBits] = useState<boolean[]>(
    Array(bitCount).fill(false)
  );
  const [inputValue, setInputValue] = useState<number>(0);

  // CORE: compute base bits from the numeric value (no dependencies applied)
  const computeBitsFromValue = (val: number): boolean[] => {
    const arr = Array(bitCount).fill(false);
    for (let i = 0; i < bitCount; i++) arr[i] = getBit(val, i) === 1;
    return arr;
  };

  // Apply dependencies in a two-phase manner (collect actions, then apply)
  const applyDependencies = (baseBits: boolean[]) => {
    if (DEV_LOG) console.log("applyDependencies - baseBits:", baseBits);

    // Collect sets/unsets/disables
    const toSet = new Set<number>();
    const toUnset = new Set<number>();
    const toDisable = new Set<number>();
    const toEnable = new Set<number>(); // children that should be enabled again when parent unchecked

    // Iterate over all label definitions based on baseBits (NOT mutated array)
    for (const [idxStr, def] of Object.entries(flatMap)) {
      const idx = Number(idxStr);
      const parentChecked = !!baseBits[idx];

      if (parentChecked) {
        // When parent is checked, schedule its setChildren to be set+disabled
        if (def.setChildren?.length) {
          for (const child of def.setChildren) {
            toSet.add(child);
            toDisable.add(child);
          }
        }
        // schedule unsetChildren to be unset+disabled
        if (def.unsetChildren?.length) {
          for (const child of def.unsetChildren) {
            toUnset.add(child);
            toDisable.add(child);
          }
        }
      } else {
        // When parent is unchecked, we must unset its children and enable them again.
        if (def.setChildren?.length) {
          for (const child of def.setChildren) {
            toUnset.add(child);
            toEnable.add(child);
          }
        }
        if (def.unsetChildren?.length) {
          for (const child of def.unsetChildren) {
            toUnset.add(child);
            toEnable.add(child);
          }
        }
      }
    }

    if (DEV_LOG) {
      //console.log("toSet:", Array.from(toSet));
      //console.log("toUnset:", Array.from(toUnset));
      //console.log("toDisable:", Array.from(toDisable));
      //console.log("toEnable:", Array.from(toEnable));
    }

    // Start from a copy of baseBits, then apply collects
    const newBits = [...baseBits];
    const newDisabled = Array(bitCount).fill(false);

    // Apply sets
    for (const idx of toSet) {
      if (idx >= 0 && idx < bitCount) newBits[idx] = true;
    }
    // Apply unsets
    for (const idx of toUnset) {
      if (idx >= 0 && idx < bitCount) newBits[idx] = false;
    }
    // Apply disables
    for (const idx of toDisable) {
      if (idx >= 0 && idx < bitCount) newDisabled[idx] = true;
    }
    // Apply enables (only enable those not currently disabled by another parent)
    // We must ensure that enabling doesn't override a disable required by another checked parent:
    // So we enable only those NOT present in toDisable.
    for (const idx of toEnable) {
      if (idx >= 0 && idx < bitCount && !toDisable.has(idx)) newDisabled[idx] = false;
    }

    if (DEV_LOG) {
      //console.log("newBits after apply:", newBits);
      //console.log("newDisabled after apply:", newDisabled);
    }

    return { newBits, newDisabled };
  };

  // Recompute bits+disabled whenever numeric value changes (including on load)
  useEffect(() => {
    const base = computeBitsFromValue(value);
    const { newBits, newDisabled } = applyDependencies(base);
    setBits(newBits);
    setDisabledBits(newDisabled);
    // keep inputValue in sync
    setInputValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, bitCount]); // bitCount stable

  // When a user toggles a checkbox
  const handleCheckChange = (position: number, checked: boolean) => {
    // Start from current bits as base (user intention)
    const baseBits = [...bits];
    baseBits[position] = checked;

    // Apply dependencies based on that base
    const { newBits, newDisabled } = applyDependencies(baseBits);

    // Compute numeric value from resulting bits
    let newValue = 0;
    newBits.forEach((b, i) => {
      if (b) newValue = setBit(newValue, i, 1);
    });

    setBits(newBits);
    setDisabledBits(newDisabled);
    setValue(newValue);
    setInputValue(newValue);
  };

  // Load numeric value (e.g., from backend)
  const handleLoadValue = () => {
    // set numeric value -> useEffect will recompute bits+disabled
    setValue(inputValue);
    message.success(`Loaded value ${inputValue}`);
  };

  // Compute Ant span
  const span = Math.max(6, Math.floor(24 / Math.max(1, categories.length)));

  return (
    <Card title="🧮 Bit Field Editor" bordered={false}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <span style={{ minWidth: 120, fontWeight: 600 }}>Decimal Value:</span>
        <InputNumber min={0} value={inputValue} onChange={(v) => setInputValue(v ?? 0)} style={{ width: 150 }} />
        <Button type="primary" onClick={handleLoadValue}>Load</Button>
      </div>

      <Row gutter={[16, 16]}>
        {categories.map(([category, labels]) => (
          <Col key={category} xs={24} sm={12} md={span}>
            <div style={{ minHeight: 80, padding: 12, borderRadius: 8, border: "1px solid rgba(0,0,0,0.06)" }}>
              <h3 style={{ textAlign: "center", marginBottom: 8 }}>{category}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(labels).map(([bitIndex, labelData]) => {
                  const bitNum = Number(bitIndex);
                  const labelObj = typeof labelData === "object" ? labelData : { label: labelData };
                  return (
                    <Checkbox
                      key={bitIndex}
                      checked={bits[bitNum]}
                      disabled={disabledBits[bitNum]}
                      onChange={(e) => handleCheckChange(bitNum, e.target.checked)}
                    >
                      {labelObj.label}
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <div><strong>Binary:</strong> {value.toString(2).padStart(bitCount, "0")}</div>
        <div><strong>Decimal:</strong> {value}</div>
      </div>
    </Card>
  );
};

export default CtrlDoc;
